import React, { useState, useEffect } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import applicationService from '../services/applicationService';
import StatusBadge from './StatusBadge';

const ApplicationDetailsModal = ({ applicationId, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    title: '',
    rawUrl: '',
    previewUrl: '',
    previewType: 'unsupported'
  });

  useEffect(() => {
    fetchApplicationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      const response = await applicationService.getApplicationById(applicationId);
      if (response.success) {
        setApplication(response.data);
        setCandidate(response.data.candidateId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvance = async () => {
    if (!canAdvance) {
      alert('This application cannot be advanced from its current stage.');
      return;
    }

    if (!window.confirm('Are you sure you want to advance this application to the next stage?')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.advanceApplication(applicationId);
      if (response.success) {
        setApplication(response.data);
        alert('Application advanced successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to advance application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!canReject) {
      alert('This application cannot be rejected from its current stage.');
      return;
    }

    if (!rejectReason.trim() || rejectReason.length < 10) {
      alert('Please provide a rejection reason (minimum 10 characters)');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.rejectApplication(applicationId, rejectReason);
      if (response.success) {
        setApplication(response.data);
        setShowRejectModal(false);
        setRejectReason('');
        alert('Application rejected');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!canWithdraw) {
      alert('This application cannot be withdrawn from its current stage.');
      return;
    }

    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.withdrawApplication(applicationId);
      if (response.success) {
        setApplication(response.data);
        alert('Application withdrawn');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to withdraw application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewFullDetails = () => {
    navigate(`/applications/${applicationId}`);
    onClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const buildDocumentPreview = (url, filename) => {
    const lowerName = String(filename || '').toLowerCase();
    const lowerUrl = String(url || '').toLowerCase();

    const isPdf = lowerName.endsWith('.pdf') || lowerUrl.includes('.pdf');
    const isImage = ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => lowerName.endsWith(ext) || lowerUrl.includes(ext));
    const isDoc = lowerName.endsWith('.doc') || lowerName.endsWith('.docx') || lowerUrl.includes('.doc') || lowerUrl.includes('.docx');

    if (isPdf) {
      return { previewType: 'pdf', previewUrl: url };
    }

    if (isImage) {
      return { previewType: 'image', previewUrl: url };
    }

    if (isDoc) {
      return {
        previewType: 'office',
        previewUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
      };
    }

    return { previewType: 'unsupported', previewUrl: '' };
  };

  const openDocumentPreview = (url, filename) => {
    if (!url) return;

    const preview = buildDocumentPreview(url, filename);

    setDocumentPreview({
      isOpen: true,
      title: filename || 'Document Preview',
      rawUrl: url,
      previewUrl: preview.previewUrl,
      previewType: preview.previewType
    });
  };

  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      title: '',
      rawUrl: '',
      previewUrl: '',
      previewType: 'unsupported'
    });
  };

  const canAdvance = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) && 
                     application?.currentStage !== 'COMPLETED' && 
                     application?.currentStage !== 'REJECTED';

  const canReject = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) && 
                    application?.currentStage !== 'REJECTED' &&
                    application?.currentStage !== 'COMPLETED';

  const canWithdraw = user?.role === 'CANDIDATE' && 
                      application?.currentStage !== 'REJECTED' &&
                      application?.currentStage !== 'COMPLETED';

  const canManageApplications = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role);
  const isCandidateUser = user?.role === 'CANDIDATE';

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
        {/* Previous Button */}
        {hasPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronLeft size={28} />
          </button>
        )}

        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 text-center text-text-secondary text-sm">Loading application...</div>
        </div>

        {/* Next Button */}
        {hasNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronRight size={28} />
          </button>
        )}
      </div>
    );
  }

  if (error || !application || !candidate) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 text-center text-text-secondary text-sm">{error || 'Application not found'}</div>
          <div className="px-4 py-2.5 border-t border-[#E0E0E0] flex justify-end gap-2">
            <button className="px-3 py-1.5 bg-[#F5F5F5] text-text-primary border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E0E0E0]" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
      {/* Previous Button */}
      {hasPrevious && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          title="Previous Application"
        >
          <MdChevronLeft size={28} />
        </button>
      )}

      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-4 py-3 border-b border-[#E0E0E0]">
          <h2 className="m-0 text-base font-bold text-primary">Application Details</h2>
          <button className="bg-transparent border-none text-[24px] text-[#999] cursor-pointer p-0 w-7 h-7 flex items-center justify-center rounded transition-all duration-200 hover:bg-[#F5F5F5] hover:text-[#333]" onClick={onClose}>&times;</button>
        </div>

        <div className="p-4">
          {/* Application Header */}
          <div className="flex justify-between items-start mb-6 gap-4">
            <div>
              <h3 className="m-0 text-lg font-bold text-primary mb-2">{application.jobPosting}</h3>
              <div className="flex items-center gap-2">
                <StatusBadge stage={application.currentStage} />
                <span className="text-sm text-text-secondary">Applied: {formatDate(application.appliedAt)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {isCandidateUser && (
                <button
                  className="px-3 py-1.5 bg-[#C76B6B] text-white border-none rounded text-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleWithdraw}
                  disabled={actionLoading}
                >
                  Withdraw
                </button>
              )}
              {canManageApplications && (
                <button
                  className="px-3 py-1.5 bg-[#C76B6B] text-white border-none rounded text-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (!canReject) {
                      alert('This application cannot be rejected from its current stage.');
                      return;
                    }
                    setShowRejectModal(true);
                  }}
                  disabled={actionLoading}
                >
                  Reject
                </button>
              )}
              {canManageApplications && (
                <button
                  className="px-3 py-1.5 bg-accent-green text-white border-none rounded text-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-[#5a6e4d] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAdvance}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : 'Advance'}
                </button>
              )}
            </div>
          </div>

          {/* Candidate Summary */}
          <div className="mb-4">
            <h4 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Candidate Summary</h4>
            <div className="flex items-start gap-4 mb-4">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                {(() => {
                  const photoUrl = candidate.profilePhoto?.url || 
                                  candidate.personalInformation?.profilePhoto?.url || 
                                  candidate.photoUrl;
                  
                  return photoUrl ? (
                    <img 
                      src={photoUrl} 
                      alt={`${candidate.firstName || candidate.personalInformation?.name || ''} ${candidate.lastName || ''}`.trim() || 'Candidate'} 
                      className="w-16 h-16 rounded-full object-cover border-2 border-accent-green shadow-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.parentNode.querySelector('.placeholder-avatar');
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <div className="placeholder-avatar w-16 h-16 rounded-full bg-gray-200 border-2 border-accent-green shadow-md flex items-center justify-center" 
                     style={{display: (() => {
                       const photoUrl = candidate.profilePhoto?.url || 
                                       candidate.personalInformation?.profilePhoto?.url || 
                                       candidate.photoUrl;
                       return photoUrl ? 'none' : 'flex';
                     })()}}>
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <h5 className="m-0 mb-2 text-sm font-semibold text-primary">
                  {candidate.personalInformation?.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate Name'}
                </h5>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Email:</span>
                    <span className="text-[11px] text-text-primary break-all">
                      {candidate.userId?.email || candidate.email || 'Not provided'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Phone:</span>
                    <span className="text-[11px] text-text-primary">
                      {candidate.personalInformation?.mobile || candidate.userId?.phoneNumber || candidate.phoneNumber || 'Not provided'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Passport:</span>
                    <span className="text-[11px] text-text-primary">
                      {candidate.personalInformation?.passportNumber || candidate.passportNumber || 'Not provided'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Gender:</span>
                    <span className="text-[11px] text-text-primary">
                      {candidate.personalInformation?.gender || candidate.gender || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Application Details */}
          <div className="mb-4">
            <h4 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Application Information</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Destination Country:</span>
                <span className="text-[11px] text-text-primary">{application.destinationCountry || 'Not specified'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Applied Date:</span>
                <span className="text-[11px] text-text-primary">{formatDate(application.appliedAt)}</span>
              </div>
              {application.completedAt && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Completed Date:</span>
                  <span className="text-[11px] text-text-primary">{formatDate(application.completedAt)}</span>
                </div>
              )}
              {application.rejectionReason && (
                <div className="flex flex-col gap-0.5 col-span-2">
                  <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Rejection Reason:</span>
                  <span className="text-[11px] text-error font-medium">
                    {application.rejectionReason}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Documents Access */}
          {(candidate.documents?.resume?.url || candidate.resumeUrl || candidate.documents?.combinedDocuments?.url || (candidate.additionalDocuments && candidate.additionalDocuments.length > 0)) && (
            <div className="mb-4">
              <h4 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Quick Document Access</h4>
              <div className="flex flex-wrap gap-2">
                {(candidate.documents?.resume?.url || candidate.resumeUrl) && (
                  <button
                    type="button"
                    onClick={() => openDocumentPreview(candidate.documents?.resume?.url || candidate.resumeUrl, 'resume.pdf')}
                    className="inline-block px-2.5 py-1.5 bg-white text-accent-green no-underline border border-accent-green rounded text-[12px] font-semibold transition-all duration-200 ease-in-out hover:bg-accent-green hover:text-white"
                  >
                    Resume/CV
                  </button>
                )}
                {candidate.documents?.combinedDocuments?.url && (
                  <button
                    type="button"
                    onClick={() => openDocumentPreview(candidate.documents.combinedDocuments.url, 'combined-documents.pdf')}
                    className="inline-block px-2.5 py-1.5 bg-white text-accent-green no-underline border border-accent-green rounded text-[12px] font-semibold transition-all duration-200 ease-in-out hover:bg-accent-green hover:text-white"
                  >
                    Combined Docs
                  </button>
                )}
                {candidate.additionalDocuments && candidate.additionalDocuments.slice(0, 2).map((doc, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => openDocumentPreview(doc.documentUrl, doc.documentName || `document-${index + 1}.pdf`)}
                    className="inline-block px-2.5 py-1.5 bg-white text-accent-green no-underline border border-accent-green rounded text-[12px] font-semibold transition-all duration-200 ease-in-out hover:bg-accent-green hover:text-white"
                  >
                    {doc.documentName || `Doc ${index + 1}`}
                  </button>
                ))}
                {candidate.additionalDocuments && candidate.additionalDocuments.length > 2 && (
                  <span className="inline-block px-2.5 py-1.5 bg-gray-100 text-text-secondary rounded text-[12px] font-semibold">
                    +{candidate.additionalDocuments.length - 2} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Application Questionnaire Preview */}
          {application.questionnaire && application.questionnaire.length > 0 && (
            <div className="mb-4">
              <h4 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Application Responses ({application.questionnaire.length})</h4>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {application.questionnaire.map((item, index) => (
                  <div key={`${item.question || 'q'}-${index}`} className="border border-gray-200 rounded-md p-2 bg-white">
                    <div className="text-[11px] font-semibold text-primary mb-1">
                      {item.question || `Question ${index + 1}`}
                    </div>
                    <div className="text-[11px] text-text-secondary whitespace-pre-wrap break-words">
                      {item.answer || 'No response provided'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-[#E0E0E0] flex justify-between gap-2">
          <button 
            className="px-3 py-1.5 bg-accent-green text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#5a6e4d]" 
            onClick={handleViewFullDetails}
          >
            View Full Details
          </button>
          <button className="px-3 py-1.5 bg-[#F5F5F5] text-text-primary border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E0E0E0]" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Next Button */}
      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          title="Next Application"
        >
          <MdChevronRight size={28} />
        </button>
      )}

      {/* Reject Modal */}
      {documentPreview.isOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.6)] flex items-center justify-center z-[1150] p-4"
          onClick={closeDocumentPreview}
        >
          <div
            className="bg-white rounded-xl w-full max-w-5xl h-[86vh] shadow-[0_12px_40px_rgba(0,0,0,0.25)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="m-0 text-sm font-semibold text-primary truncate pr-4">{documentPreview.title}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={documentPreview.rawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-primary no-underline hover:bg-gray-200"
                >
                  Open in New Tab
                </a>
                <button
                  type="button"
                  onClick={closeDocumentPreview}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold bg-accent-green text-white hover:bg-[#5a6e4d]"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-[calc(86vh-56px)] bg-gray-50">
              {documentPreview.previewType === 'image' && (
                <div className="w-full h-full overflow-auto flex items-center justify-center bg-white">
                  <img src={documentPreview.previewUrl} alt={documentPreview.title} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              {(documentPreview.previewType === 'pdf' || documentPreview.previewType === 'office') && (
                <iframe
                  src={documentPreview.previewUrl}
                  title={documentPreview.title}
                  className="w-full h-full border-0"
                />
              )}
              {documentPreview.previewType === 'unsupported' && (
                <div className="w-full h-full flex items-center justify-center text-center px-6">
                  <div>
                    <p className="text-sm text-text-secondary mb-3">Preview is not available for this file type.</p>
                    <a
                      href={documentPreview.rawUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-2 px-4 rounded-md text-xs font-semibold bg-accent-green text-white no-underline hover:bg-[#5a6e4d]"
                    >
                      Open File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.7)] flex items-center justify-center z-[1100]" onClick={() => setShowRejectModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-2 text-lg font-bold text-primary">Reject Application</h3>
            <p className="m-0 mb-4 text-text-secondary text-sm">Please provide a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason (minimum 10 characters)"
              rows={4}
              minLength={10}
              maxLength={500}
              className="w-full p-3 border border-gray-300 rounded-lg font-[inherit] text-sm resize-vertical mb-4 focus:outline-none focus:border-accent-green"
            />
            <div className="flex gap-3 justify-end">
              <button
                className="px-3 py-1.5 bg-[#F5F5F5] text-text-primary border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E0E0E0] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 bg-[#C76B6B] text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleReject}
                disabled={actionLoading || rejectReason.length < 10}
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetailsModal;
